/**
 * ServicesManagementPage.jsx - Garage Services Management
 * 
 * Allows garage owners/managers to:
 * - View all services
 * - Add new services
 * - Edit existing services
 * - Delete/disable services
 */

import React, { useState, useEffect } from 'react';

const smartCapitalize = (str) => str ? str.replace(/\b[a-z]/g, c => c.toUpperCase()) : str;
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Wrench,
  Clock,
  DollarSign,
  Settings,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default service categories
const DEFAULT_CATEGORIES = [
  { value: 'maintenance', label: 'Maintenance', labelBn: 'রক্ষণাবেক্ষণ', isDefault: true },
  { value: 'repair', label: 'Repair', labelBn: 'মেরামত', isDefault: true },
  { value: 'washing', label: 'Washing', labelBn: 'ওয়াশিং', isDefault: true },
  { value: 'electrical', label: 'Electrical', labelBn: 'ইলেকট্রিক্যাল', isDefault: true },
  { value: 'custom', label: 'Custom Work', labelBn: 'কাস্টম কাজ', isDefault: true },
];

// Empty service form template
const emptyService = {
  name: '',
  name_bn: '',
  category: 'maintenance',
  price: '',
  estimated_time: '',
  description: ''
};

export default function ServicesManagementPage() {
  const { garageSlug } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyService);
  const [saving, setSaving] = useState(false);

  // Category management state
  const [showCategorySettings, setShowCategorySettings] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryNameBn, setNewCategoryNameBn] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [disabledDefaults, setDisabledDefaults] = useState([]);

  // Confirm dialog state (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });

  // Build combined categories list (only active custom categories)
  const allCategories = [
    ...DEFAULT_CATEGORIES.filter(c => !disabledDefaults.includes(c.value)),
    ...customCategories.filter(c => c.is_active !== false).map(c => ({ value: c.name.toLowerCase().replace(/\s+/g, '_'), label: c.name, labelBn: c.name_bn || c.name, id: c.id }))
  ];

  // Fetch services on mount
  useEffect(() => {
    fetchServices();
    fetchCustomCategories();
    fetchCategorySettings();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast({ title: language === 'en' ? 'Failed to load services' : 'সার্ভিস লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/services`);
      setCustomCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCategorySettings = async () => {
    try {
      const response = await axios.get(`${API}/category-settings/services`);
      setDisabledDefaults(response.data.disabled_defaults || []);
    } catch (error) {
      console.error('Failed to fetch category settings:', error);
    }
  };

  // Category management handlers
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCategoryName.trim(), name_bn: newCategoryNameBn.trim() || null, module: 'services' });
      toast({ title: language === 'en' ? 'Category added!' : 'ক্যাটাগরি যোগ হয়েছে!' });
      setNewCategoryName('');
      setNewCategoryNameBn('');
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to add category' : 'ক্যাটাগরি যোগ ব্যর্থ', variant: 'destructive' });
    }
  };

  const handleUpdateCategory = async (catId) => {
    if (!editingCategory) return;
    try {
      await axios.put(`${API}/categories/${catId}`, { name: editingCategory.name, name_bn: editingCategory.name_bn || null, module: 'services' });
      toast({ title: language === 'en' ? 'Category updated!' : 'ক্যাটাগরি আপডেট হয়েছে!' });
      setEditingCategory(null);
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to update' : 'আপডেট ব্যর্থ', variant: 'destructive' });
    }
  };

  const handleDeleteCategory = (catId) => {
    setConfirmDialog({
      open: true,
      title: language === 'en' ? 'Delete Category' : 'ক্যাটাগরি মুছুন',
      description: language === 'en' ? 'Are you sure you want to delete this category?' : 'আপনি কি নিশ্চিত যে এই ক্যাটাগরিটি মুছে ফেলতে চান?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/categories/${catId}`);
          toast({ title: language === 'en' ? 'Category deleted!' : 'ক্যাটাগরি মুছে ফেলা হয়েছে!' });
          fetchCustomCategories();
        } catch (error) {
          toast({ title: language === 'en' ? 'Failed to delete' : 'মুছতে ব্যর্থ', variant: 'destructive' });
        }
      }
    });
  };

  const handleToggleCustomCategory = async (catId) => {
    try {
      await axios.patch(`${API}/categories/${catId}/toggle`);
      fetchCustomCategories();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to toggle' : 'টগল ব্যর্থ', variant: 'destructive' });
    }
  };

  const toggleDefaultCategory = async (value) => {
    const newDisabled = disabledDefaults.includes(value)
      ? disabledDefaults.filter(v => v !== value)
      : [...disabledDefaults, value];
    setDisabledDefaults(newDisabled);
    try {
      await axios.put(`${API}/category-settings/services`, { disabled_defaults: newDisabled });
    } catch (error) {
      console.error('Failed to save category settings:', error);
    }
  };

  // Open dialog for adding new service
  const handleAddService = () => {
    setEditingService(null);
    setFormData(emptyService);
    setShowDialog(true);
  };

  // Open dialog for editing service
  const handleEditService = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name || '',
      name_bn: service.name_bn || '',
      category: service.category || 'maintenance',
      price: service.price?.toString() || '',
      estimated_time: service.estimated_time || '',
      description: service.description || ''
    });
    setShowDialog(true);
  };

  // Save service (create or update)
  const handleSaveService = async () => {
    // Validation
    if (!formData.name || !formData.price) {
      toast({ title: language === 'en' ? 'Name and price are required' : 'নাম এবং মূল্য আবশ্যক', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingService) {
        // Update existing service
        await axios.put(`${API}/services/${editingService.id}`, payload);
        toast({ title: language === 'en' ? 'Service updated!' : 'সার্ভিস আপডেট হয়েছে!' });
      } else {
        // Create new service
        await axios.post(`${API}/services`, payload);
        toast({ title: language === 'en' ? 'Service added!' : 'সার্ভিস যোগ হয়েছে!' });
      }

      setShowDialog(false);
      fetchServices();
    } catch (error) {
      console.error('Failed to save service:', error);
      toast({ title: language === 'en' ? 'Failed to save' : 'সংরক্ষণ ব্যর্থ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Delete service
  const handleDeleteService = (serviceId) => {
    setConfirmDialog({
      open: true,
      title: language === 'en' ? 'Delete Service' : 'সার্ভিস মুছুন',
      description: language === 'en' ? 'Are you sure you want to delete this service? This action cannot be undone.' : 'আপনি কি নিশ্চিত যে এই সার্ভিসটি মুছে ফেলতে চান? এই ক্রিয়াটি পূর্বাবস্থায় ফেরানো যাবে না।',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/services/${serviceId}`);
          toast({ title: language === 'en' ? 'Service deleted!' : 'সার্ভিস মুছে ফেলা হয়েছে!' });
          fetchServices();
        } catch (error) {
          console.error('Failed to delete service:', error);
          toast({ title: language === 'en' ? 'Failed to delete' : 'মুছতে ব্যর্থ', variant: 'destructive' });
        }
      }
    });
  };

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = !search || 
      service.name.toLowerCase().includes(search.toLowerCase()) ||
      service.name_bn?.includes(search);
    const matchesCategory = categoryFilter === 'all' || service.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
              {language === 'en' ? 'Services' : 'সার্ভিস'}
            </h1>
            <p className="text-slate-500 text-sm">
              {language === 'en' ? 'Manage your garage services' : 'আপনার গ্যারেজ সার্ভিস পরিচালনা করুন'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowCategorySettings(true)}
              data-testid="service-category-settings-btn"
            >
              <Settings className="w-4 h-4" />
              {language === 'en' ? 'Categories' : 'ক্যাটাগরি'}
            </Button>
            <Button onClick={handleAddService} className="gap-2" data-testid="add-service-btn">
              <Plus className="w-4 h-4" />
              {language === 'en' ? 'Add Service' : 'সার্ভিস যোগ করুন'}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder={language === 'en' ? 'Search services...' : 'সার্ভিস খুঁজুন...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 pr-9"
                />
                {search && (
                  <button
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600 transition-colors"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'en' ? 'All Categories' : 'সব ক্যাটাগরি'}</SelectItem>
                  {allCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {language === 'bn' ? cat.labelBn : cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Services Grid */}
        {filteredServices.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {language === 'en' ? 'No services found' : 'কোনো সার্ভিস পাওয়া যায়নি'}
              </p>
              <Button onClick={handleAddService} variant="outline" className="mt-4">
                {language === 'en' ? 'Add Your First Service' : 'প্রথম সার্ভিস যোগ করুন'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredServices.map(service => (
              <Card key={service.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-slate-900">
                        {language === 'bn' && service.name_bn ? service.name_bn : service.name}
                      </h3>
                      <Badge variant="secondary" className="mt-1 capitalize">
                        {service.category}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEditService(service)}
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteService(service.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {service.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{service.description}</p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{service.estimated_time || '-'}</span>
                    </div>
                    <span className="text-xl font-bold text-blue-800">
                      {formatCurrency(service.price, language)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingService 
                  ? (language === 'en' ? 'Edit Service' : 'সার্ভিস সম্পাদনা') 
                  : (language === 'en' ? 'Add New Service' : 'নতুন সার্ভিস যোগ করুন')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Name (English)' : 'নাম (ইংরেজি)'} *</Label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    onBlur={() => setFormData(p => ({ ...p, name: smartCapitalize(p.name) }))}
                    placeholder="Oil Change"
                    data-testid="service-name"
                  />
                </div>
                <div>
                  <Label>{language === 'en' ? 'Name (Bengali)' : 'নাম (বাংলা)'}</Label>
                  <Input 
                    value={formData.name_bn}
                    onChange={(e) => setFormData(p => ({ ...p, name_bn: e.target.value }))}
                    placeholder="তেল পরিবর্তন"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'en' ? 'Category' : 'ক্যাটাগরি'}</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}
                  >
                    <SelectTrigger data-testid="service-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allCategories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {language === 'bn' ? cat.labelBn : cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{language === 'en' ? 'Price (৳)' : 'মূল্য (৳)'} *</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="1"
                    value={formData.price}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9]/g, '');
                      setFormData(p => ({ ...p, price: cleaned }));
                    }}
                    onKeyDown={(e) => { if (['.', 'e', '-', '+'].includes(e.key)) e.preventDefault(); }}
                    placeholder="500"
                    data-testid="service-price"
                  />
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Estimated Time' : 'আনুমানিক সময়'}</Label>
                <Input 
                  value={formData.estimated_time}
                  onChange={(e) => setFormData(p => ({ ...p, estimated_time: e.target.value }))}
                  placeholder="30 mins"
                  data-testid="service-time"
                />
              </div>

              <div>
                <Label>{language === 'en' ? 'Description' : 'বিবরণ'}</Label>
                <Textarea 
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder={language === 'en' ? 'Describe what this service includes...' : 'এই সার্ভিসে কী অন্তর্ভুক্ত তা বর্ণনা করুন...'}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowDialog(false)}>
                  {language === 'en' ? 'Cancel' : 'বাতিল'}
                </Button>
                <Button onClick={handleSaveService} disabled={saving} data-testid="save-service">
                  {saving ? <Spinner size="sm" className="border-white" /> : null}
                  {language === 'en' ? 'Save' : 'সংরক্ষণ'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Category Settings Dialog */}
        <Dialog open={showCategorySettings} onOpenChange={setShowCategorySettings}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Manage Service Categories' : 'সার্ভিস ক্যাটাগরি পরিচালনা'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Default categories with toggle */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Default Categories' : 'ডিফল্ট ক্যাটাগরি'}</p>
                <div className="space-y-2">
                  {DEFAULT_CATEGORIES.map(cat => (
                    <div key={cat.value} className="flex items-center justify-between p-2 rounded-lg border bg-slate-50">
                      <span className="text-sm">{language === 'bn' ? cat.labelBn : cat.label}</span>
                      <Button
                        variant={disabledDefaults.includes(cat.value) ? 'outline' : 'default'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => toggleDefaultCategory(cat.value)}
                      >
                        {disabledDefaults.includes(cat.value)
                          ? (language === 'en' ? 'Disabled' : 'নিষ্ক্রিয়')
                          : (language === 'en' ? 'Active' : 'সক্রিয়')
                        }
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom categories */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Custom Categories' : 'কাস্টম ক্যাটাগরি'}</p>
                <div className="space-y-2">
                  {customCategories.map(cat => (
                    <div key={cat.id} className="flex items-center gap-2 p-2 rounded-lg border">
                      {editingCategory?.id === cat.id ? (
                        <div className="flex flex-col gap-1.5 w-full">
                          <div className="flex gap-2">
                            <Input
                              value={editingCategory.name}
                              onChange={(e) => setEditingCategory(p => ({ ...p, name: e.target.value }))}
                              className="h-7 text-sm flex-1"
                              placeholder={language === 'en' ? 'English name' : 'ইংরেজি নাম'}
                            />
                            <Input
                              value={editingCategory.name_bn || ''}
                              onChange={(e) => setEditingCategory(p => ({ ...p, name_bn: e.target.value }))}
                              className="h-7 text-sm flex-1"
                              placeholder={language === 'en' ? 'বাংলা নাম' : 'বাংলা নাম'}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" className="h-7 text-xs" onClick={() => handleUpdateCategory(cat.id)}>
                              {language === 'en' ? 'Save' : 'সংরক্ষণ'}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingCategory(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className={`text-sm flex-1 ${cat.is_active === false ? 'text-slate-400 line-through' : ''}`}>
                            {cat.name}{cat.name_bn ? ` (${cat.name_bn})` : ''}
                          </span>
                          <Button
                            size="sm"
                            variant={cat.is_active === false ? 'outline' : 'default'}
                            className="h-7 text-xs"
                            onClick={() => handleToggleCustomCategory(cat.id)}
                          >
                            {cat.is_active === false
                              ? (language === 'en' ? 'Disabled' : 'নিষ্ক্রিয়')
                              : (language === 'en' ? 'Active' : 'সক্রিয়')
                            }
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingCategory(cat)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => handleDeleteCategory(cat.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                  {customCategories.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">{language === 'en' ? 'No custom categories yet' : 'এখনো কোনো কাস্টম ক্যাটাগরি নেই'}</p>
                  )}
                </div>
              </div>

              {/* Add new category */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-2">{language === 'en' ? 'Add New Category' : 'নতুন ক্যাটাগরি যোগ করুন'}</p>
                <div className="flex gap-2">
                  <Input
                    placeholder={language === 'en' ? 'Name (English)' : 'নাম (ইংরেজি)'}
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Input
                    placeholder={language === 'en' ? 'Name (বাংলা)' : 'নাম (বাংলা)'}
                    value={newCategoryNameBn}
                    onChange={(e) => setNewCategoryNameBn(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Button size="sm" className="h-9" onClick={handleAddCategory}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Custom Confirm Dialog (replaces browser confirm) */}
        <AlertDialog open={confirmDialog.open} onOpenChange={(open) => { if (!open) setConfirmDialog(p => ({ ...p, open: false })); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDialog.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmDialog(p => ({ ...p, open: false }))}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => { confirmDialog.onConfirm?.(); setConfirmDialog(p => ({ ...p, open: false })); }}
              >
                {language === 'en' ? 'Delete' : 'মুছুন'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
